import { useCareHistory, type CareHistoryFilter } from "@/lib/api/care";
import { PostHogEvents, trackEvent } from "@/lib/posthog-events";
import { cn } from "@/lib/utils";
import { Check, Loader2 } from "lucide-react";
import { useState } from "react";
import { CareHistoryRow } from "./care-history-row";

const FILTERS: { id: CareHistoryFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "assessments", label: "Assessments" },
  { id: "presentation", label: "Presentation" },
  { id: "care_updates", label: "Care Updates" },
  { id: "flags", label: "Flags" },
  { id: "escalations", label: "Escalations" },
];

export function CareHistory({
  menteeId,
  billing,
}: {
  menteeId: string;
  billing: boolean;
}) {
  const [filter, setFilter] = useState<CareHistoryFilter>("all");
  const filters = billing
    ? FILTERS
    : FILTERS.filter(
        (option) =>
          option.id !== "presentation" && option.id !== "care_updates",
      );
  const history = useCareHistory(menteeId, filter);
  const items = history.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-heading text-base font-bold leading-6 tracking-[-0.4px] text-[#1f1e1d]">
        History
      </h2>
      <div className="flex flex-wrap gap-2">
        {filters.map((option) => {
          const active = option.id === filter;
          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={active}
              onClick={() => {
                trackEvent(PostHogEvents.CARE_PAGE_HISTORY_FILTER_CLICKED, {
                  mentee_id: menteeId,
                  filter: option.label,
                });
                setFilter(option.id);
              }}
              className={cn(
                "inline-flex h-8 min-w-12 items-center justify-center gap-1 rounded-md border border-[#cbc6be] px-2 font-body text-xs leading-4 text-[#1f1e1d]",
                active && "bg-[#d4cbb5]",
              )}
            >
              {active ? <Check className="size-4" aria-hidden /> : null}
              {option.label}
            </button>
          );
        })}
      </div>
      <div className="mt-1 overflow-hidden rounded-3xl border border-[#cbc6be] bg-white">
        {history.isPending ? (
          <Centered>
            <Loader2 className="size-5 animate-spin text-[#a0998d]" />
          </Centered>
        ) : history.isError ? (
          <Centered>
            <p className="font-body text-sm text-states-error">
              Unable to load history.{" "}
              <button
                type="button"
                onClick={() => void history.refetch()}
                className="underline"
              >
                Retry
              </button>
            </p>
          </Centered>
        ) : items.length === 0 ? (
          <Centered>
            <p className="font-body text-sm text-[#58534b]">
              Nothing here yet.
            </p>
          </Centered>
        ) : (
          <ul>
            {items.map((item) => (
              <CareHistoryRow
                key={`${item.type}-${item.id}`}
                menteeId={menteeId}
                item={item}
              />
            ))}
          </ul>
        )}
      </div>
      {history.hasNextPage ? (
        <button
          type="button"
          disabled={history.isFetchingNextPage}
          onClick={() => {
            trackEvent(PostHogEvents.CARE_PAGE_HISTORY_SHOW_15_MORE_CLICKED, {
              mentee_id: menteeId,
            });
            history.fetchNextPage();
          }}
          className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-full border border-[rgba(0,0,0,0.16)] bg-[rgba(160,153,141,0.08)] px-4 font-body text-sm leading-5 text-[#1f1e1d] disabled:opacity-50"
        >
          {history.isFetchingNextPage ? (
            <Loader2 className="size-4 animate-spin" />
          ) : null}
          Show 15 more
        </button>
      ) : null}
    </section>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex justify-center px-4 py-6">{children}</div>;
}
